/**
 * identity-service password-reset client (bl-777) — the forgot-password /
 * reset-password front-door wiring.
 *
 * Hits `POST {VITE_IDENTITY_SERVICE_URL}/forgot-password` + `/reset-password`
 * (verified against MERGED identity-service `src/password_reset_handlers.rs` +
 * `src/password_reset.rs`). The email + the new password are sent ONLY over HTTPS
 * in the request body and are NEVER logged, stored, or echoed.
 *
 * ANTI-ENUMERATION (load-bearing — do NOT undo on the client). `/forgot-password`
 * returns a UNIFORM 202 with uniform latency whether or not the email maps to an
 * account (the divergent DB-lookup → token-issue → email-send work is detached
 * server-side). So our success result is the SAME for every outcome — the UI never
 * branches on existence. `/reset-password` likewise returns a UNIFORM
 * `400 {error:"invalid or expired token"}` for ANY token miss (unknown / expired /
 * consumed / hash-mismatch), distinct from a weak-password `400 {error:<reason>}`.
 */

/** The fixed, non-enumerating token-failure message the server returns (see
 *  identity-service `ResetError::Failed` → `"invalid or expired token"`). We match
 *  on it to distinguish a token miss from a correctable weak-password 400. */
const TOKEN_FAILURE_MESSAGE = 'invalid or expired token';

function identityServiceUrl(): string {
  const base = import.meta.env.VITE_IDENTITY_SERVICE_URL;
  if (!base) {
    // Surfaced as a generic error in the UI; the deploy build-arg wires this.
    throw new Error('VITE_IDENTITY_SERVICE_URL is not configured');
  }
  return base.replace(/\/$/, '');
}

/**
 * Outcome of a forgot-password request.
 *
 *  - `sent` is the UNIFORM success — returned for 202 (and defensively for any
 *    non-5xx). It carries NO information about whether the email exists; the UI
 *    shows the same neutral copy for it always.
 *  - `error` is a network / 5xx failure (generic retry). It still reveals nothing
 *    about existence.
 */
export type RequestResetResult = { kind: 'sent' } | { kind: 'error' };

/**
 * `POST /forgot-password {email}`. The server is the security boundary (uniform
 * 202, detached work, no timing oracle); we map 202 — and any non-5xx — to the
 * SAME neutral `sent`, so the UI cannot become an enumeration oracle. Only a
 * network or 5xx fault is surfaced as `error` (still existence-free).
 */
export async function requestPasswordReset(
  email: string,
): Promise<RequestResetResult> {
  let res: Response;
  try {
    res = await fetch(`${identityServiceUrl()}/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch {
    // Network/CORS failure — generic, existence-free.
    return { kind: 'error' };
  }

  // 5xx → generic retry. Everything else (202 + any other non-5xx) collapses to
  // the SAME neutral `sent` — no branch can reveal whether the email exists.
  if (res.status >= 500) {
    return { kind: 'error' };
  }
  return { kind: 'sent' };
}

/**
 * Outcome of a reset-password confirm.
 *
 *  - `reset` — 200, the password was set; the UI offers a sign-in link.
 *  - `weakPassword` — 400 with a static strength `reason` (the server validated
 *    the new password and rejected it WITHOUT consuming the token). The form is
 *    retained so the user can correct it.
 *  - `tokenFailed` — the uniform token miss (unknown / expired / consumed /
 *    mismatch). Terminal: the link is dead, the user must request a new one.
 *  - `error` — 500 / network / unexpected (generic retry).
 */
export type ConfirmResetResult =
  | { kind: 'reset' }
  | { kind: 'weakPassword'; reason: string }
  | { kind: 'tokenFailed' }
  | { kind: 'error' };

interface ResetErrorBody {
  error?: string;
}

/**
 * `POST /reset-password {token, new_password}`. Classifies the response into the
 * distinct UI branches. A 400 is disambiguated by its body: the fixed
 * `"invalid or expired token"` message → `tokenFailed` (terminal); any other 400
 * reason is a weak-password rejection → `weakPassword` (correctable, form kept).
 */
export async function confirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<ConfirmResetResult> {
  let res: Response;
  try {
    res = await fetch(`${identityServiceUrl()}/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  } catch {
    return { kind: 'error' };
  }

  if (res.status === 200) {
    return { kind: 'reset' };
  }

  if (res.status === 400) {
    let body: ResetErrorBody = {};
    try {
      body = (await res.json()) as ResetErrorBody;
    } catch {
      // A 400 with an unreadable body — treat as the terminal token failure
      // (fail-closed: do not present a correctable weak-password form on garbage).
      return { kind: 'tokenFailed' };
    }
    const message = body.error ?? '';
    if (message === TOKEN_FAILURE_MESSAGE) {
      return { kind: 'tokenFailed' };
    }
    // Any other 400 reason is a static weak-password strength message.
    return { kind: 'weakPassword', reason: message || 'That password is not allowed.' };
  }

  // 500 + any unexpected status → generic error.
  return { kind: 'error' };
}
